// @ts-nocheck
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType, InventoryAvailabilityDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import getColor from "@insite/mobius/utilities/getColor";
import Tooltip from "@insite/mobius/Tooltip";
import Icon from "@insite/mobius/Icon";
import siteMessage from "@insite/client-framework/SiteMessage";
import AlertCircleFill, {
    AlertCircleFillOffset,
} from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleFill";
import AlertTriangleFill, {
    AlertTriangleFillOffset,
} from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleFill";
import CheckmarkCircle2Fill, {
    CheckmarkCircle2FillOffset,
} from "@store/Library/Components/Toolkit/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import { StatusColor, statusStyles } from "@store/Themes/common/mixins/status";
import InfoLine from "@store/Library/Components/Toolkit/Icons/Info/InfoLine";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC } from "react";
import styled, { keyframes } from "styled-components";

// Palette lifted straight from ValveConfiguratorBomWidget's FLS map so this
// component inherits the storefront's Mobius theme tokens (primary, text.main,
// warning.light, success.main, etc.) rather than shipping its own hard-coded
// hex values. Stock states use the same tokens as the ProductAvailability
// override (success / warning / danger).
const TOKENS = {
    cta: getColor("primary"),
    ink: getColor("text.main"),
    text: getColor("text.main"),
    muted: getColor("text.accent"),
    subtle: getColor("placeholder"),
    border: getColor("common.border"),
    warnBg: getColor("warning.light"),
    warnBorder: getColor("warning.main"),
    inStockFg: getColor("success.main"),
    inStockBg: getColor("success.light"),
    lowStockFg: getColor("warning.main"),
    lowStockBg: getColor("warning.light"),
    outOfStockFg: getColor("danger.main"),
    outOfStockBg: getColor("danger.light"),
} as const;

type StockKind = "in" | "low" | "out" | "unknown";

interface UnitHeaderCardProps {
    partNumber: string;
    modelNumber: string;
    partsListNumber: string;
    /** Human-readable product name (typically from bomPrices response). Falls back to modelNumber. */
    unitName?: string;
    /** Unit price × unitQty; drives the label on the CTA. */
    unitPricePerAssembly: number;
    unitPriceDisplay: string;
    /** BOM line count for the "sum of {n} line items" caption. */
    bomLineCount: number;
    unitQty: number;
    onUnitQtyChange: (qty: number) => void;
    /** Coarse stock state derived from the BOM inventory. Pass undefined to hide the badge while inventory loads. */
    unitStock?: StockKind;
    unitStockLabel?: string;
    /** Optional pre-formatted shipping caption, e.g. "Estimated shipping: Sep 12, 2026". */
    unitShippingCaption?: string;
    /** Canonical PDP path for the Unit PN; renders the part number as a link when present. */
    productDetailPath?: string;
    /** When false, we render "Request a Quote" instead of Add to Cart and skip cart plumbing. */
    isOrderable: boolean;
    onRequestQuote: () => void;
    /**
     * Pre-built ProductContext for the Unit PN, supplied by the parent so we do
     * not duplicate the getProductCollectionV2 + realtimepricing + realtimeinventory
     * calls that <c>useBomPricing</c> already made. When undefined, the CTA and
     * Add-to-List link fall back to the quote flow.
     */
    productContext?: ProductContextModel;
    /** While true the card renders a skeleton only — no price, stock or CTA is shown. */
    loading?: boolean;
}

const pulse = keyframes`
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.45;
    }
`;

const SkeletonBar = styled.div<{ $w: number; $h: number }>`
    width: ${({ $w }) => pxToRem($w)};
    height: ${({ $h }) => pxToRem($h)};
    max-width: 100%;
    border-radius: ${pxToRem(4)};
    background: ${TOKENS.border};
    animation: ${pulse} 1.2s ease-in-out infinite;
`;

const SkeletonNote = styled.div`
    font-size: ${pxToRem(13)};
    color: ${TOKENS.muted};
`;

const Card = styled.section`
    background: #ffffff;
    border: 1px solid ${TOKENS.border};
    border-radius: ${pxToRem(16)};
    padding: ${pxToRem(24)};
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(16)};

    @media (max-width: 600px) {
        padding: ${pxToRem(16)};
        border-radius: ${pxToRem(8)};
    }
`;

const HeaderRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${pxToRem(20)};

    @media (max-width: 600px) {
        gap: ${pxToRem(12)};
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(6)};
    flex: 1 1 ${pxToRem(260)};
    min-width: 0;
`;

const UnitTitle = styled.h2`
    margin: 0;
    font-size: ${pxToRem(24)};
    line-height: 1.25;
    color: ${TOKENS.ink};
    font-weight: 700;
    overflow-wrap: anywhere;

    @media (max-width: 600px) {
        font-size: ${pxToRem(20)};
    }
`;

const UnitPartNo = styled.div`
    font-size: ${pxToRem(14)};
    color: ${TOKENS.muted};
    overflow-wrap: anywhere;
`;

const UnitTitleLink = styled.a`
    color: inherit;
    text-decoration: none;

    &:hover,
    &:focus {
        text-decoration: underline;
        color: ${TOKENS.cta};
    }
`;

const HeaderRight = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${pxToRem(6)};
    margin-left: auto;

    @media (max-width: 600px) {
        align-items: flex-start;
        margin-left: 0;
        width: 100%;
    }
`;

const PriceLabel = styled.div`
    font-size: ${pxToRem(12)};
    font-weight: 600;
    color: ${TOKENS.muted};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const PriceValue = styled.div`
    font-size: ${pxToRem(28)};
    font-weight: 700;
    color: ${TOKENS.ink};
    font-variant-numeric: tabular-nums;
`;

const PriceCaption = styled.div`
    font-size: ${pxToRem(13)};
    color: ${TOKENS.muted};
    text-align: right;
    max-width: ${pxToRem(260)};

    @media (max-width: 600px) {
        text-align: left;
        max-width: none;
    }
`;

const Hairline = styled.div`
    height: 1px;
    background: ${TOKENS.border};
`;

const StatusRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${pxToRem(16)};
`;

const StatusLeft = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${pxToRem(10)};
    align-items: center;
    flex: 1 1 ${pxToRem(300)};
`;

const StatusRight = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${pxToRem(12)};
    margin-left: auto;

    @media (max-width: 600px) {
        margin-left: 0;
        width: 100%;

        /* Let the Add to Cart / Request a quote CTA fill the row under the qty stepper. */
        > *:last-child {
            flex: 1 1 100%;
        }
    }
`;

// Same pill treatment as the ProductAvailability override so the configurator
// matches PLP/PDP: shared statusStyles mixin + toolkit icons, no bespoke colors.
const StatusPill = styled.div<{ _color: StatusColor; _iconOffset?: IconOffset }>`
    ${x => statusStyles(x._color, x._iconOffset)}
    border-radius: ${pxToRem(28)};
    width: fit-content;
    white-space: nowrap;
`;

/* Mirrors styles.leadTimeMessage: the lead tone has no fill, so it needs the outline. */
const LeadTimePill = styled(StatusPill)`
    border: 1px solid ${TOKENS.border};
`;

// Same info glyph the ProductAvailability override uses for its tooltips.
const tooltipProps = {
    iconProps: {
        src: InfoLine,
        color: "swatch.black",
    },
} as const;

const StatusPillRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${pxToRem(6)};
`;

const stockPresentation: Record<
    StockKind,
    { color: StatusColor; iconSrc: React.ComponentType<any>; iconOffset: IconOffset }
> = {
    in: { color: "success", iconSrc: CheckmarkCircle2Fill, iconOffset: CheckmarkCircle2FillOffset },
    low: { color: "warning", iconSrc: AlertTriangleFill, iconOffset: AlertTriangleFillOffset },
    out: { color: "danger", iconSrc: AlertTriangleFill, iconOffset: AlertTriangleFillOffset },
    unknown: { color: "neutral", iconSrc: AlertCircleFill, iconOffset: AlertCircleFillOffset },
};

const QtyLabel = styled.label`
    display: flex;
    align-items: center;
    gap: ${pxToRem(8)};
    font-size: ${pxToRem(13)};
    color: ${TOKENS.muted};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

const QtyInput = styled.input`
    width: ${pxToRem(56)};
    padding: ${pxToRem(8)} ${pxToRem(10)};
    border: 1px solid ${TOKENS.border};
    border-radius: ${pxToRem(4)};
    font-size: ${pxToRem(14)};
    text-align: center;
`;

const PrimaryButton = styled.button`
    background: ${TOKENS.cta};
    color: #ffffff;
    border: none;
    border-radius: ${pxToRem(4)};
    padding: ${pxToRem(11)} ${pxToRem(22)};
    font-size: ${pxToRem(14)};
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: ${pxToRem(8)};

    &:hover,
    &:focus {
        filter: brightness(0.92);
    }

    @media (max-width: 600px) {
        width: 100%;
    }
`;

const WarningBox = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${pxToRem(8)};
    background: ${TOKENS.warnBg};
    border: 1px solid ${TOKENS.warnBorder};
    border-radius: ${pxToRem(4)};
    padding: ${pxToRem(10)} ${pxToRem(12)};
    font-size: ${pxToRem(13)};
    color: ${TOKENS.ink};
`;

const AddToListRow = styled.div`
    /* Nudges the OOTB Add to List link to the design's link style (13px, weight 600); color inherits from theme. */
    a,
    button {
        color: ${TOKENS.cta};
        font-size: ${pxToRem(13)};
        font-weight: 600;
    }
`;

const WarningIconSlot = styled.span`
    color: ${TOKENS.warnBorder};
    display: inline-flex;
    line-height: 0;
`;

/**
 * Renders the unit-price card at the top of the BOM per the redesign. Orderable
 * state uses the shared ProductContext + Insite Add to Cart pipeline; quote
 * state renders a simple "Request a Quote" button that the parent wires to a
 * scroll-into-view of the quote form.
 */
const UnitHeaderCard: FC<UnitHeaderCardProps> = ({
    partNumber,
    modelNumber,
    partsListNumber,
    unitName,
    unitPricePerAssembly,
    unitPriceDisplay,
    bomLineCount,
    unitQty,
    onUnitQtyChange,
    unitStock,
    unitStockLabel,
    unitShippingCaption,
    isOrderable,
    onRequestQuote,
    productContext,
    loading,
    productDetailPath,
}) => {
    if (loading) {
        return (
            <Card aria-busy="true" data-test-selector="valveUnitHeaderLoading">
                <HeaderRow>
                    <HeaderLeft>
                        <SkeletonBar $w={280} $h={26} />
                        <SkeletonBar $w={180} $h={14} />
                    </HeaderLeft>
                    <HeaderRight>
                        <SkeletonBar $w={140} $h={12} />
                        <SkeletonBar $w={120} $h={30} />
                    </HeaderRight>
                </HeaderRow>
                <Hairline />
                <SkeletonNote>{translate("FlsValveConfigurator.Bom.LoadingPrices")}</SkeletonNote>
            </Card>
        );
    }

    const displayName = unitName || modelNumber;
    const priceLabel = isOrderable
        ? translate("FlsValveConfigurator.Bom.PriceLabelOrderable")
        : translate("FlsValveConfigurator.Bom.PriceLabelQuote");
    const captionOrderable = translate(
        "FlsValveConfigurator.Bom.CaptionOrderable",
        `${unitQty}`,
        `${bomLineCount}`,
    );
    const captionQuote = translate("FlsValveConfigurator.Bom.CaptionQuote");
    const captionNoPrice = translate("FlsValveConfigurator.Bom.CaptionNoPrice");
    const cartLabel = translate("FlsValveConfigurator.Bom.AddToCartLabel", unitPriceDisplay);
    const quoteRequiredNotice = translate("FlsValveConfigurator.Qto.RequiredNotice");

    // Keeps the exact list of props Insite's Add-to-Cart button expects to
    // avoid runtime prop-mismatch warnings. `partsListNumber` is intentionally
    // included in the destructure so styling audits can find it; not passed on.
    void modelNumber;
    void partsListNumber;

    return (
        <Card>
            <HeaderRow>
                <HeaderLeft>
                    <UnitTitle>
                        {productDetailPath ? (
                            <UnitTitleLink href={productDetailPath}>{displayName}</UnitTitleLink>
                        ) : (
                            displayName
                        )}
                    </UnitTitle>
                    <UnitPartNo>{`Part #: ${partNumber}`}</UnitPartNo>
                </HeaderLeft>
                <HeaderRight>
                    {unitPricePerAssembly > 0 ? (
                        <>
                            <PriceLabel>{priceLabel}</PriceLabel>
                            <PriceValue>{unitPriceDisplay}</PriceValue>
                            <PriceCaption>{isOrderable ? captionOrderable : captionQuote}</PriceCaption>
                        </>
                    ) : (
                        <PriceCaption>{captionNoPrice}</PriceCaption>
                    )}
                </HeaderRight>
            </HeaderRow>

            <Hairline />

            <StatusRow>
                <StatusLeft>
                    {unitStock && unitStockLabel ? (
                        <StatusPillRow>
                            <StatusPill
                                _color={stockPresentation[unitStock].color}
                                _iconOffset={stockPresentation[unitStock].iconOffset}
                            >
                                <Icon
                                    color={stockPresentation[unitStock].color}
                                    src={stockPresentation[unitStock].iconSrc}
                                />
                                <span>{unitStockLabel}</span>
                            </StatusPill>
                            <Tooltip
                                {...tooltipProps}
                                text={siteMessage("RealTimeInventory_AvailabilityTooltip").toString()}
                            />
                        </StatusPillRow>
                    ) : null}
                    {unitShippingCaption ? (
                        <StatusPillRow>
                            <LeadTimePill _color="lead" _iconOffset={CheckmarkCircle2FillOffset}>
                                <Icon color="lead" src={CheckmarkCircle2Fill} />
                                <span>{unitShippingCaption}</span>
                            </LeadTimePill>
                            <Tooltip
                                {...tooltipProps}
                                text={siteMessage("RealTimeInventory_EstimatedDeliveryTooltip").toString()}
                            />
                        </StatusPillRow>
                    ) : null}
                </StatusLeft>
                <StatusRight>
                    <QtyLabel>
                        {translate("FlsValveConfigurator.Bom.Qty")}
                        <QtyInput
                            type="number"
                            min={1}
                            value={unitQty}
                            onChange={e => {
                                const v = parseInt(e.target.value, 10);
                                onUnitQtyChange(Number.isFinite(v) && v > 0 ? v : 1);
                            }}
                        />
                    </QtyLabel>
                    {isOrderable && productContext ? (
                        <ProductContext.Provider value={productContext}>
                            <ProductAddToCartButton labelOverride={cartLabel} />
                        </ProductContext.Provider>
                    ) : (
                        <PrimaryButton type="button" onClick={onRequestQuote}>
                            {translate("FlsValveConfigurator.Qto.RequestQuote")}
                        </PrimaryButton>
                    )}
                </StatusRight>
            </StatusRow>

            {!isOrderable ? (
                <WarningBox>
                    <WarningIconSlot>
                        <Icon color="warning" src={AlertTriangleFill} size={20} />
                    </WarningIconSlot>
                    <span>{quoteRequiredNotice}</span>
                </WarningBox>
            ) : null}

            {isOrderable && productContext ? (
                <ProductContext.Provider value={productContext}>
                    <AddToListRow>
                        <ProductAddToListLink />
                    </AddToListRow>
                </ProductContext.Provider>
            ) : null}
        </Card>
    );
};

/**
 * Convenience helper: derive the coarse unit stock (in / low / out) from a
 * collection of BOM line inventory DTOs. Reads the availability entry for the
 * incoming UOM (defaults to "EA" like the storefront-standard hooks).
 */
export function deriveUnitStock(
    lines: Array<InventoryAvailabilityDto | undefined>,
    unitOfMeasure = "EA",
): { stock: StockKind; label: string } {
    let anyOut = false;
    let anyLow = false;
    for (const dto of lines) {
        if (!dto) {
            continue;
        }
        const availability = dto.availability;
        if (!availability) {
            continue;
        }
        if (availability.messageType === AvailabilityMessageType.OutOfStock) {
            anyOut = true;
        } else if (availability.messageType === AvailabilityMessageType.LowStock) {
            anyLow = true;
        }
    }
    if (anyOut) {
        return { stock: "out", label: "Extended Delivery Time" };
    }
    if (anyLow) {
        return { stock: "low", label: "Low stock" };
    }
    void unitOfMeasure;
    return { stock: "in", label: "In stock" };
}

export default UnitHeaderCard;
